import { useState, useEffect } from "react";
import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Spinner, SpinnerSize, DefaultButton, Text, TextField, PrimaryButton, Dropdown, IDropdownOption, Label, Checkbox } from "@fluentui/react";
import { getSuErrors, getSuCompanies, downloadSuErrors } from "../../api/superuser/api_superuser";
import { SuErrorInfo, SuCompanyInfo } from "../../api/superuser/models_superuser";
import { AdminLabel, CellContent, ErrorDialog } from "../admin/components";

export const SuperuserErrors = () => {
    const [errorsData, setErrorsData] = useState<SuErrorInfo[]>([]);
    const [filteredErrorsData, setFilteredErrorsData] = useState<SuErrorInfo[]>([]);
    const [companies, setCompanies] = useState<SuCompanyInfo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [datetimeFrom, setDatetimeFrom] = useState<string>("");
    const [datetimeTo, setDatetimeTo] = useState<string>("");
    const [filterCompanyCd, setFilterCompanyCd] = useState<number | null | undefined>(undefined);
    const [filterMessageText, setFilterMessageText] = useState<string>("");
    const [showLoginLimitErrorOnly, setShowLoginLimitErrorOnly] = useState<boolean>(false);
    const [isDownloading, setIsDownloading] = useState<boolean>(false);

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        applyFilters();
    }, [errorsData, filterCompanyCd, filterMessageText, showLoginLimitErrorOnly]);

    const fetchCompanies = async () => {
        try {
            const companiesData = await getSuCompanies();
            if ("error" in companiesData) {
                setError(companiesData.error);
            } else {
                setCompanies(companiesData);
            }
        } catch (err) {
            setError("会社情報の取得中にエラーが発生しました");
        }
    };

    const fetchErrors = async (from?: string, to?: string) => {
        setLoading(true);
        setError(null);
        try {
            const data = await getSuErrors(from, to);
            if ("error" in data) {
                setError(data.error);
            } else {
                setErrorsData(data);
            }
        } catch (err) {
            setError("エラー情報の取得中にエラーが発生しました");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const result = await downloadSuErrors(datetimeFrom || undefined, datetimeTo || undefined);
            if (result && "error" in result) {
                setError(result.error);
            }
        } catch (err) {
            setError("ダウンロード中にエラーが発生しました");
        } finally {
            setIsDownloading(false);
        }
    }

    const applyFilters = () => {
        let filtered = [...errorsData];

        if (filterCompanyCd !== undefined) {
            if (filterCompanyCd === null) {
                filtered = filtered.filter(item => item.company_cd === null);
            } else {
                filtered = filtered.filter(item => item.company_cd === filterCompanyCd);
            }
        }

        if (filterMessageText) {
            const searchText = filterMessageText.toLowerCase();
            filtered = filtered.filter(item => 
                item.message?.toLowerCase().includes(searchText)
            );
        }

        if (showLoginLimitErrorOnly) {
            filtered = filtered.filter(item => 
                item.message?.includes("ログイン人数上限到達")
            );
        }

        setFilteredErrorsData(filtered);
    };

    const handleSearch = () => {
        fetchErrors(datetimeFrom || undefined, datetimeTo || undefined);
    };

    const handleReset = () => {
        setDatetimeFrom("");
        setDatetimeTo("");
        setFilterCompanyCd(undefined);
        setFilterMessageText("");
        setShowLoginLimitErrorOnly(false);
    };

    const areaStyle ={
        root: { 
            padding: 20, 
            border: '1px solid #e1e1e1', 
            borderRadius: 4,
            backgroundColor: '#fafafa'
        }
    }

    const handleCompanyChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option?.key === 'all') {
            setFilterCompanyCd(undefined);
        } else if (option?.key === 'null') {
            setFilterCompanyCd(null);
        } else {
            setFilterCompanyCd(Number(option?.key));
        }
    };

    const companyOptions: IDropdownOption[] = [
        { key: 'all', text: '全会社' },
        { key: 'null', text: '会社コードなし' },
        ...companies.map(company => ({
            key: company.company_cd,
            text: `${company.company_name} (${company.company_cd})`
        }))
    ];

    const formatDateTime = (datetime: string | null): string => {
        if (!datetime) return "-";
        try {
            const date = new Date(datetime);
            return date.toLocaleString('ja-JP', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        } catch {
            return datetime;
        }
    };

    const errorColumns: IColumn[] = [
        {
            key: 'seq',
            name: 'SEQ',
            minWidth: 60,
            maxWidth: 80,
            isResizable: true,
            onRender: (item: SuErrorInfo) => (
                <CellContent>{item.seq}</CellContent>
            )
        },
        {
            key: 'entry_datetime',
            name: '発生日時',
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            onRender: (item: SuErrorInfo) => (
                <CellContent>{formatDateTime(item.entry_datetime)}</CellContent>
            )
        },
        {
            key: 'company_cd',
            name: '会社コード',
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            onRender: (item: SuErrorInfo) => (
                <CellContent>{item.company_cd ?? "-"}</CellContent>
            )
        },
        {
            key: 'userid',
            name: 'ユーザーID',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: SuErrorInfo) => (
                <CellContent>{item.userid ?? "-"}</CellContent>
            )
        },
        {
            key: 'message',
            name: 'エラーメッセージ',
            minWidth: 300,
            isResizable: true,
            isMultiline: true,
            onRender: (item: SuErrorInfo) => (
                <div style={{ 
                    fontSize: "11pt", 
                    fontWeight: 400, 
                    color: "black",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    padding: "8px 0"
                }}>
                    {item.message}
                </div>
            )
        }
    ];

    if (loading) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" style={{ minHeight: '400px' }}>
                <Spinner size={SpinnerSize.large} label="読み込み中..." />
            </Stack>
        );
    }

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <AdminLabel text="エラーログ照会" />
            {
                <ErrorDialog 
                    content={error ?? ""} 
                    onDismiss={() => setError(null)} 
                    isOpen={Boolean(error)} />
            }

            <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end" styles={areaStyle}>
                <TextField
                    label="開始日時"
                    type="datetime-local"
                    value={datetimeFrom}
                    onChange={(_, newValue) => setDatetimeFrom(newValue || "")}
                    styles={{ root: { width: 250 } }}
                    disabled={isDownloading}
                />
                <TextField
                    label="終了日時"
                    type="datetime-local"
                    value={datetimeTo}
                    onChange={(_, newValue) => setDatetimeTo(newValue || "")}
                    styles={{ root: { width: 250 } }}
                    disabled={isDownloading}
                />
                <DefaultButton text="リセット" onClick={handleReset} iconProps={{ iconName: 'Clear' }} disabled={isDownloading} />
                <PrimaryButton text="検索" onClick={handleSearch} iconProps={{ iconName: 'Search' }} disabled={isDownloading} />
                <Stack.Item grow>
                    <Stack horizontal horizontalAlign="end">
                        <PrimaryButton text={`ダウンロード`} onClick={handleDownload} iconProps={{ iconName: 'Download' }} disabled={isDownloading} />
                    </Stack>
                </Stack.Item>
            </Stack>
            
            <Stack styles={areaStyle} tokens={{ childrenGap: 8 }}>
                <Label style={{fontSize: "14pt"}}>フィルター条件</Label>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                    <Dropdown
                        placeholder="会社を選択"
                        label="会社コード"
                        options={companyOptions}
                        selectedKey={filterCompanyCd === undefined ? 'all' : filterCompanyCd === null ? 'null' : filterCompanyCd}
                        onChange={handleCompanyChange}
                        styles={{ dropdown: { width: 300 } }}
                        disabled={isDownloading}
                    />
                    <TextField
                        label="エラーメッセージ検索"
                        placeholder="検索キーワード"
                        value={filterMessageText}
                        onChange={(_, newValue) => setFilterMessageText(newValue || "")}
                        styles={{ root: { width: 300 } }}
                        disabled={isDownloading}
                    />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="end">
                    <Checkbox
                        label="ログイン上限到達エラーのみ表示"
                        checked={showLoginLimitErrorOnly}
                        onChange={(_, checked) => setShowLoginLimitErrorOnly(checked || false)}
                        disabled={isDownloading}
                    />
                </Stack>
            </Stack>

            <Text variant="large" block>
                エラー件数: {filteredErrorsData.length} 件 {errorsData.length !== filteredErrorsData.length && `(全${errorsData.length}件)`}
            </Text>

            <DetailsList
                items={filteredErrorsData}
                columns={errorColumns}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
            />
        </Stack>
    );
}